import { useState } from "react";
import {
  listKeys,
  enrollWebauthn,
  enrollSoftware,
  removeKey,
  renameKey,
  webauthnSupported,
  type EnrolledKey,
} from "../lib/unlock";

// Modal for managing tap-to-unlock keys. Enroll several (primary + backups);
// any of them unlocks all rooms and transcripts.
export function UnlockSettings({ username, onClose }: { username: string; onClose: () => void }) {
  const [keys, setKeys] = useState<EnrolledKey[]>(listKeys());
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setKeys(listKeys());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const addingBackup = keys.length > 0;

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="card" style={{ width: 500 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>Unlock keys</h2>
        <p className="muted" style={{ marginTop: -8 }}>
          Enroll one or more security keys. <b>Any</b> enrolled key unlocks all your rooms and
          transcripts with a tap — and lets you <b>sign in</b> by tapping (no password). Enroll a
          backup key so you're never locked out. Nothing usable is stored on disk without a key.
        </p>

        {keys.length > 0 && (
          <div className="field">
            <div className="muted" style={{ marginBottom: 4 }}>Enrolled on this browser:</div>
            {keys.map((k) => (
              <div
                key={k.id}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}
              >
                <span>{k.kind === "webauthn" ? "🔑" : "🧪"}</span>
                <span style={{ flex: 1 }}>
                  {k.label}
                  {k.kind === "software" && <span className="muted"> (test)</span>}
                </span>
                <button
                  className="linkbtn"
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      const n = prompt("Rename this key:", k.label);
                      if (n && n.trim()) renameKey(k.id, n.trim());
                    })
                  }
                >
                  rename
                </button>
                <button className="linkbtn" disabled={busy} onClick={() => run(async () => removeKey(k.id))}>
                  remove
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            placeholder="Name this key (e.g. YubiKey blue, work laptop)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button
            className="primary"
            disabled={busy || !webauthnSupported()}
            onClick={() => run(() => enrollWebauthn(username, name.trim() || undefined).then(() => setName("")))}
          >
            {busy ? "Tap your key…" : keys.length ? "🔑 Add another security key" : "🔑 Enroll a security key (YubiKey)"}
          </button>
          {!webauthnSupported() && (
            <p className="muted" style={{ margin: 0 }}>WebAuthn isn't available in this browser.</p>
          )}
          <button disabled={busy} onClick={() => run(() => enrollSoftware(name.trim() || undefined).then(() => setName("")))}>
            🧪 Add a software test key (no hardware)
          </button>
          {addingBackup && (
            <p className="muted" style={{ margin: 0, fontSize: 11 }}>
              Adding a key taps an existing key first (to carry over your unlock), then the new one.
            </p>
          )}
        </div>

        {error && <p className="error">{error}</p>}
        <button className="ghost" onClick={onClose} style={{ marginTop: 12 }}>
          Close
        </button>
      </div>
    </div>
  );
}
